"use client";
import { useEffect, useState } from "react";
import moment from "moment";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
    AdaptiveModal,
    AdaptiveModalContent,
    AdaptiveModalDescription,
    AdaptiveModalFooter,
    AdaptiveModalHeader,
    AdaptiveModalTitle,
    AdaptiveModalTrigger,
} from "@/components/tomui/adaptive.modal";
import {
    IconArrowBack,
    IconFolder,
    IconFolderOpen,
    IconFolderUp,
    IconGrid4x4,
    IconGridDots,
    IconLayoutGrid,
    IconList,
    IconPhoto,
    IconTrash,
    IconVideo,
} from "@tabler/icons-react";
import {
    IAccountAssets,
    IBlocks,
    IUserData,
    IUsersBrandAccounts,
} from "@/types";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PopoverClose } from "@radix-ui/react-popover";
import AssetsUpload from "./upload-v3";
import { Input } from "@/components/ui/input";

import PageHeading from "../layout/PageHeading";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useQueryState } from "nuqs";
import useLocalStorage from "@/hooks/useLocalStorage";
import { Checkbox } from "@/components/ui/checkbox";

interface Props {
    _assets: IAccountAssets[];
    impersonate?: IUserData;
    loggedInUser?: IUsersBrandAccounts | undefined;
    block?: IBlocks;
    update?: (value: IAccountAssets) => void;
    account_id?: string;
    _className?: string;
    sidebarPicker?: boolean;
    popover?: boolean;
    account: IUserData;
}

export default function Assets({
    _assets,
    impersonate,
    loggedInUser,
    block,
    update,
    account_id,
    _className,
    sidebarPicker,
    popover,
    account,
}: Props) {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [selectedAssets, setSelectedAssets] = useState<Set<string>>(
        new Set()
    );
    const toggleAssetSelection = (assetId: string | null | undefined) => {
        if (assetId) {
            setSelectedAssets((prev) => {
                const newSet = new Set(prev);
                if (newSet.has(assetId)) {
                    newSet.delete(assetId);
                } else {
                    newSet.add(assetId);
                }
                return newSet;
            });
        }
    };
    // const assets = assetsDemo;
    const [assets, setAssets] = useState<IAccountAssets[]>(_assets || []);
    const [searchQuery, setSearchQuery] = useState("");
    const filteredAssets = assets.filter((asset) =>
        asset.title.toLowerCase().includes(searchQuery?.toLowerCase() || "")
    );
    useEffect(() => {
        if (account_id) {
            const fetchAssets = async () => {
                try {
                    const res = await fetch(
                        `/api/account/${account_id}/assets`
                    );
                    const data = await res.json();
                    setAssets(data.data.assets);
                } catch (error) {
                    console.error("Failed to fetch assets:", error);
                }
            };
            fetchAssets();
        }
    }, [account_id]); // Add account_id to the dependency array
    const folderCounts = filteredAssets.reduce(
        (acc, asset) => {
            if (asset.folder) {
                acc[asset.folder] = (acc[asset.folder] || 0) + 1;
            }
            return acc;
        },
        {} as Record<string, number>
    );
    const uniqueFolders = Object.keys(folderCounts);
    const noFolderAssets = filteredAssets.filter((asset) => !asset.folder);
    const [selectedFolder, setSelectedFolder] = useQueryState("folder");
    const assetsInSelectedFolder = selectedFolder
        ? filteredAssets.filter((asset) => asset.folder === selectedFolder)
        : [];
    const [selectedAsset, setSelectedAsset] = useState<IAccountAssets | null>(
        null
    );
    const [folderName, setFolderName] = useState("");
    const [title, setTitle] = useState("");
    function addAsset(asset: IAccountAssets) {
        setAssets((prevAssets) => [...prevAssets, asset]);
    }
    useEffect(() => {
        if (!isModalOpen) {
            setSelectedAsset(null);
        }
    }, [isModalOpen, selectedAsset]);
    async function RemoveAsset(id: any, file: any) {
        try {
            return await fetch(
                process.env.NEXT_PUBLIC_API_SERVER_INTERNAL +
                    "/api/admin/assets/remove",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        id: id,
                        file: file,
                        impersonate: impersonate ? impersonate.id : null,
                    }),
                }
            );
        } catch (error) {
            console.error("Error uploading file:", error);
            const customError: Response = new Response(
                JSON.stringify({
                    error: error,
                })
            );
            return customError;
        }
    }
    const deleteSelectedAssets = async () => {
        const deletePromises = Array.from(selectedAssets).map((assetId) => {
            const asset = assets.find((a) => a.id === assetId);
            if (asset) {
                return RemoveAsset(asset.id, asset.url);
            }
            return Promise.resolve();
        });

        toast.promise(Promise.all(deletePromises), {
            loading: "Deleting selected assets...",
            success: () => {
                setAssets((prevAssets) =>
                    prevAssets.filter(
                        (asset) => !selectedAssets.has(asset.id || "")
                    )
                );
                setSelectedAssets(new Set());
                return "Assets deleted successfully";
            },
            error: "Failed to delete some assets",
        });
    };
    // Asset Management
    const [viewMode, setViewMode] = useLocalStorage<"grid" | "list">(
        "AssetsViewMode",
        "grid"
    );

    return (
        <div
            className={cn(
                "flex w-full items-start gap-3 overflow-auto",
                !block && "pl-2",
                _className
            )}
        >
            <div
                className={cn(
                    "noscroll w-full overflow-hidden bg-transparent py-2",
                    popover && "overflow-y-auto"
                )}
            >
                <div className={cn("flex flex-col gap-3 pb-3")}>
                    {!block && (
                        <>
                            <PageHeading
                                title="Your Assets"
                                icon={<IconPhoto />}
                                color="tertiary"
                                subtitle="Upload and manage your custom assets & images"
                            />
                            <div className=" ml-auto flex items-center gap-3 rounded-md bg-muted">
                                <Input
                                    placeholder="Search assets..."
                                    value={searchQuery!}
                                    onChange={(e) =>
                                        setSearchQuery(e.target.value)
                                    }
                                    className="w-[200px]"
                                    variant={"card"}
                                />
                                <Separator
                                    orientation="vertical"
                                    className="h-6 bg-accent"
                                />
                                <Button
                                    variant="outline"
                                    className="bg-accent"
                                    onClick={() =>
                                        setViewMode(
                                            viewMode === "grid"
                                                ? "list"
                                                : "grid"
                                        )
                                    }
                                >
                                    {viewMode === "grid" ? (
                                        <div className="flex items-center gap-2">
                                            <IconList /> List View
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <IconLayoutGrid /> Grid View
                                        </div>
                                    )}
                                </Button>
                                {selectedAssets.size > 0 && (
                                    <Button
                                        variant="destructive"
                                        onClick={deleteSelectedAssets}
                                    >
                                        <IconTrash className="mr-2" />
                                        Delete Selected ({selectedAssets.size})
                                    </Button>
                                )}
                            </div>
                        </>
                    )}
                </div>
                <div
                    className={cn(
                        "h-full overflow-y-scroll",
                        typeof update === "function" && block ? "" : "w-full"
                    )}
                >
                    <div
                        className={cn(
                            "grid w-full shrink-0 overflow-y-auto",
                            typeof update === "function" && block
                                ? "grid grid-cols-2 gap-2"
                                : viewMode === "grid"
                                  ? "grid-cols-2 gap-3 md:grid-cols-4"
                                  : "gap-0 md:grid-cols-1"
                        )}
                    >
                        {loggedInUser?.role != "user" && !selectedFolder && (
                            <AssetsUpload
                                addAsset={addAsset}
                                viewMode={viewMode}
                                account={account}
                            />
                        )}
                        {!selectedFolder &&
                            uniqueFolders.map((folder) => (
                                <div
                                    className={cn(
                                        "cursor-pointer overflow-hidden rounded-lg border bg-card shadow transition hover:bg-muted hover:shadow-lg",
                                        viewMode === "list" &&
                                            "flex items-center gap-4 rounded-none border-b border-b-border last:border-0",
                                        assets.find(
                                            (asset) =>
                                                (asset.url === block?.url &&
                                                    asset.folder === folder) ||
                                                (asset.url ===
                                                    selectedAsset?.url &&
                                                    asset.folder === folder)
                                        )
                                            ? "border-primary"
                                            : "border-transparent"
                                    )}
                                    key={folder}
                                    onClick={() => setSelectedFolder(folder)}
                                >
                                    <IconFolder
                                        className={cn(
                                            "object-cover",
                                            viewMode === "grid"
                                                ? "h-56 w-full p-12"
                                                : "h-20 w-20"
                                        )}
                                    />
                                    <div
                                        className={cn(
                                            "flex items-center justify-between gap-2 bg-card px-2 pb-3 pt-1",
                                            viewMode === "list" &&
                                                "flex-col items-start gap-0 bg-transparent p-0"
                                        )}
                                    >
                                        {viewMode === "list" && (
                                            <Label
                                                variant="headingtoggle"
                                                className="text-xs"
                                            >
                                                Folder
                                            </Label>
                                        )}
                                        <Badge>{folder}</Badge>
                                        <span className="text-xs text-muted-foreground">
                                            {folderCounts[folder]} assets
                                        </span>
                                    </div>
                                </div>
                            ))}
                        {selectedFolder && (
                            <>
                                <div
                                    className={cn(
                                        "cursor-pointer overflow-hidden rounded-lg border bg-card shadow transition hover:bg-muted hover:shadow-lg",
                                        viewMode === "list" &&
                                            "flex items-center gap-4 rounded-none border-b border-b-border last:border-0"
                                    )}
                                    key="back"
                                    onClick={() => setSelectedFolder(null)}
                                >
                                    <IconArrowBack
                                        className={cn(
                                            "object-cover",
                                            viewMode === "grid"
                                                ? "h-56 w-full p-12"
                                                : "h-20 w-20"
                                        )}
                                    />
                                    <div
                                        className={cn(
                                            "flex items-center justify-between gap-2 bg-card px-2 pb-3 pt-1",
                                            viewMode === "list" &&
                                                "bg-transparent p-0"
                                        )}
                                    >
                                        <Label
                                            variant="headingtoggle"
                                            className="text-xs"
                                        >
                                            Back
                                        </Label>
                                    </div>
                                </div>
                                {assetsInSelectedFolder.map((asset, index) => (
                                    <AssetItem
                                        asset={asset}
                                        key={index}
                                        viewMode={viewMode}
                                        setSelectedAsset={(asset) => {
                                            if (
                                                selectedAsset?.id === asset.id
                                            ) {
                                                setSelectedAsset(null);
                                                setFolderName("");
                                                setTitle("");
                                                // setGlobalOpen(false);
                                                setIsModalOpen(false);
                                            } else {
                                                setSelectedAsset(asset);
                                                setFolderName(
                                                    asset.folder || ""
                                                );
                                                setTitle(asset.title);
                                                // setGlobalOpen(true);
                                                setIsModalOpen(true);
                                            }
                                        }}
                                        block={block}
                                        update={update}
                                        selectedAsset={selectedAsset}
                                        isSelected={selectedAssets.has(
                                            asset.id || ""
                                        )}
                                        toggleSelection={() =>
                                            toggleAssetSelection(asset.id)
                                        }
                                        setIsModalOpen={setIsModalOpen}
                                    />
                                ))}
                            </>
                        )}
                        {!selectedFolder && (
                            <>
                                {noFolderAssets.map((asset, index) => (
                                    <AssetItem
                                        asset={asset}
                                        key={index}
                                        viewMode={viewMode}
                                        setSelectedAsset={(asset) => {
                                            if (
                                                selectedAsset?.id === asset.id
                                            ) {
                                                setSelectedAsset(null);
                                                setFolderName("");
                                                setTitle("");
                                                // setGlobalOpen(false);
                                                setIsModalOpen(false);
                                            } else {
                                                setSelectedAsset(asset);
                                                setFolderName(
                                                    asset.folder || ""
                                                );
                                                setTitle(asset.title);
                                                // setGlobalOpen(true);
                                                setIsModalOpen(true);
                                            }
                                        }}
                                        block={block}
                                        update={update}
                                        selectedAsset={selectedAsset}
                                        isSelected={selectedAssets.has(
                                            asset.id || ""
                                        )}
                                        toggleSelection={() =>
                                            toggleAssetSelection(asset.id)
                                        }
                                        setIsModalOpen={setIsModalOpen}
                                    />
                                ))}
                            </>
                        )}
                    </div>
                </div>
            </div>
            {typeof update !== "function" && !block && (
                <AdaptiveModal
                    variant="sheet"
                    open={isModalOpen}
                    onOpenChange={setIsModalOpen}
                >
                    <AdaptiveModalContent className="z-50">
                        <AdaptiveModalHeader>
                            <AdaptiveModalTitle>
                                {selectedAsset
                                    ? selectedAsset.title
                                    : "Asset Details"}
                            </AdaptiveModalTitle>
                            <AdaptiveModalDescription>
                                {selectedAsset
                                    ? "Uploaded " +
                                      moment(selectedAsset.created_at).format(
                                          "MMMM Do, YYYY"
                                      )
                                    : "Select an asset to view details"}
                            </AdaptiveModalDescription>
                        </AdaptiveModalHeader>

                        <div className="flex flex-col gap-3">
                            {selectedAsset ? (
                                <div className="aspect-video w-full rounded-md border-2 border-dashed border-foreground/50 bg-foreground/20 object-contain">
                                    <img
                                        alt=""
                                        src={selectedAsset?.url || ""}
                                        className="h-full w-full object-contain"
                                    />
                                </div>
                            ) : (
                                <Skeleton
                                    className="aspect-video w-full rounded-md border-2 border-dashed border-foreground/50 bg-foreground/20 object-contain"
                                    nopulse
                                />
                            )}
                            <div className="flex flex-col gap-3 px-4">
                                {selectedAsset && (
                                    <>
                                        <Label
                                            variant={"toggle"}
                                            className="mb-0"
                                        >
                                            Title
                                        </Label>
                                        <Input
                                            className="w-full"
                                            value={title}
                                            onChange={(e) =>
                                                setTitle(e.target.value)
                                            }
                                        />
                                        <Label
                                            variant={"toggle"}
                                            className="mb-0"
                                        >
                                            Folder
                                        </Label>
                                        <Input
                                            className="w-full"
                                            value={folderName}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                // Allow only letters, numbers, hyphens, and underscores
                                                const filteredValue =
                                                    value.replace(
                                                        /[^a-zA-Z0-9-_]/g,
                                                        ""
                                                    );
                                                // Update the folder state or variable with the filtered value
                                                setFolderName(filteredValue);
                                            }}
                                        />
                                        {(title !== selectedAsset.title ||
                                            (folderName?.length === 0
                                                ? null
                                                : folderName) !==
                                                selectedAsset.folder) && (
                                            <Button
                                                onClick={() => {
                                                    const loader =
                                                        toast.loading(
                                                            "Saving..."
                                                        );
                                                    let headers = new Headers();
                                                    headers.append(
                                                        "Content-Type",
                                                        "application/json"
                                                    );
                                                    if (impersonate) {
                                                        headers.append(
                                                            "brand-id",
                                                            impersonate.id
                                                        );
                                                    }
                                                    fetch(
                                                        "/api/admin/assets/edit",
                                                        {
                                                            method: "POST",
                                                            headers: headers,
                                                            body: JSON.stringify(
                                                                {
                                                                    id: selectedAsset.id,
                                                                    title: title,
                                                                    folder: folderName
                                                                        ? folderName.length >
                                                                          0
                                                                            ? folderName
                                                                            : null
                                                                        : null,
                                                                }
                                                            ),
                                                        }
                                                    )
                                                        .then((res) =>
                                                            res.json()
                                                        )
                                                        .then((data) => {
                                                            if (data.assets) {
                                                                setAssets(
                                                                    data.assets
                                                                );
                                                            }
                                                            toast.success(
                                                                "Saved",
                                                                {
                                                                    id: loader,
                                                                }
                                                            );
                                                            setSelectedAsset({
                                                                ...selectedAsset,
                                                                title: title,
                                                                folder: folderName,
                                                            });
                                                            setFolderName(
                                                                folderName
                                                            );
                                                            setTitle(title);
                                                        })
                                                        .catch((err) => {
                                                            toast.error(
                                                                "Error updating",
                                                                {
                                                                    id: loader,
                                                                }
                                                            );
                                                        });
                                                }}
                                            >
                                                Save
                                            </Button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        <AdaptiveModalFooter>
                            {selectedAsset ? (
                                <Popover>
                                    <PopoverTrigger
                                        asChild
                                        disabled={
                                            impersonate &&
                                            loggedInUser?.role === "user"
                                        }
                                    >
                                        <Button variant="destructive">
                                            <IconTrash className="mr-2" />{" "}
                                            Delete asset
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent>
                                        <Label>
                                            Are you sure you want to delete this
                                            asset?
                                        </Label>
                                        <Label variant="subtoggle">
                                            If it's being used somewhere, it
                                            will be removed from there as well.
                                        </Label>
                                        <div className="mt-3 flex items-center gap-3">
                                            <Button
                                                onClick={() => {
                                                    toast.promise(
                                                        RemoveAsset(
                                                            selectedAsset.id,
                                                            selectedAsset.url
                                                        ),
                                                        {
                                                            loading:
                                                                "Removing asset...",
                                                            success: (data) => {
                                                                setAssets(
                                                                    (
                                                                        prevAssets
                                                                    ) =>
                                                                        prevAssets.filter(
                                                                            (
                                                                                asset
                                                                            ) =>
                                                                                asset.id !==
                                                                                selectedAsset.id
                                                                        )
                                                                );
                                                                setSelectedAsset(
                                                                    null
                                                                );
                                                                return "Asset removed successfully";
                                                            },
                                                            error: (error) => {
                                                                return `Error: ${error}`;
                                                            },
                                                        }
                                                    );
                                                }}
                                            >
                                                <IconTrash /> Delete
                                            </Button>
                                            <PopoverClose asChild>
                                                <Button>Cancel</Button>
                                            </PopoverClose>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            ) : (
                                <Skeleton className="h-3 w-24" nopulse />
                            )}
                        </AdaptiveModalFooter>
                    </AdaptiveModalContent>
                </AdaptiveModal>
            )}
        </div>
    );
}

function AssetItem({
    asset,
    setSelectedAsset,
    block,
    update,
    selectedAsset,
    viewMode = "grid",
    isSelected,
    toggleSelection,
    setIsModalOpen, // Add this prop
}: {
    asset: IAccountAssets;
    setSelectedAsset: (asset: IAccountAssets) => void;
    block?: IBlocks;
    update?: (value: IAccountAssets) => void;
    selectedAsset: IAccountAssets | null | undefined;
    viewMode?: "grid" | "list";
    isSelected: boolean;
    toggleSelection: () => void;
    setIsModalOpen: (open: boolean) => void; // Add this to the interface
}) {
    if (asset.url.endsWith(".webm")) {
        return (
            <div
                className={cn(
                    "group cursor-not-allowed overflow-hidden rounded-lg border-2 border-dashed border-primary/30 bg-muted/40 shadow-sm transition hover:bg-muted/60",
                    "flex flex-col items-center justify-center p-6 text-center text-sm text-muted-foreground"
                )}
            >
                <IconVideo className="mb-2 h-8 w-8 text-primary/70" />
                <p className="font-semibold text-foreground">
                    Video assets are only available using our new theme engine.
                </p>
                <a
                    href="https://doras.to/posts/theme-enginev2"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 text-sm font-medium text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()} // prevents modal opening if clicked
                >
                    Learn more
                </a>
            </div>
        );
    }
    return (
        <div
            className={cn(
                "group cursor-pointer overflow-hidden rounded-lg border-2 shadow transition hover:border-primary/70 hover:bg-muted hover:shadow-lg",
                asset.url == block?.url || selectedAsset?.url == asset.url
                    ? "border-primary"
                    : "border-transparent",
                viewMode === "list" &&
                    "flex items-center gap-4 rounded-none border-b-2 border-b-border last:border-b-transparent",
                viewMode === "list" &&
                    selectedAsset?.url == asset.url &&
                    "border-b-primary bg-primary/10 last:border-b-primary",
                isSelected && "bg-primary/20"
            )}
            onClick={() => {
                if (typeof update === "function" && block) {
                    if (asset.url !== block.url) {
                        update(asset);
                    }
                } else {
                    // Use the setSelectedAsset function as provided from the parent
                    // This will handle both selection and deselection
                    setSelectedAsset(asset);
                    // No need to manually set modal state here as it's handled in the setSelectedAsset function
                }
            }}
        >
            <div
                className={cn("relative", viewMode === "list" && "flex gap-2")}
            >
                {!block && (
                    <Checkbox
                        checked={isSelected}
                        onCheckedChange={toggleSelection}
                        className={cn(
                            "",
                            viewMode === "grid"
                                ? "absolute left-2 top-2 hidden group-hover:block data-[state=checked]:block"
                                : "absolute left-0 top-0 m-1"
                        )}
                        onClick={(e) => e.stopPropagation()}
                    />
                )}
                <img
                    alt={asset.title}
                    src={asset.url}
                    className={cn(
                        "object-cover",
                        viewMode === "grid" ? "h-56 w-full" : "h-20 w-20"
                    )}
                />
            </div>
            <div
                className={cn(
                    "flex items-center justify-between gap-2 bg-card px-2 pb-3 pt-1",
                    viewMode === "list" && "bg-transparent p-0"
                )}
            >
                <p className="select-none truncate text-sm font-bold">
                    {asset.title}
                </p>
                <p className="select-none text-nowrap text-right text-xs text-muted-foreground">
                    {moment(asset.created_at).format("MMMM Do, YYYY")}
                </p>
            </div>
        </div>
    );
}
